import { MenuItemData } from '../menu-item-data'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useMenuContext } from './use-menu-context'

export const useMenu = (
  items: MenuItemData[]
): {
  itemRefs: MutableRefObject<(HTMLLIElement | null)[]>
  onMouseLeave: () => void
  onMouseMove: () => void
} => {
  const { selected, setSelected, showFocus, setShowFocus } = useMenuContext()

  const onMouseLeave = useCallback(() => {
    setSelected(undefined)
    setShowFocus(false)
  }, [setShowFocus, setSelected])

  const itemRefs = useRef<Array<HTMLLIElement | null>>([])

  const onMouseMove = useCallback(() => {
    if (showFocus) {
      setShowFocus(false)
    }
  }, [showFocus, setShowFocus])

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items?.length)
  }, [items])

  useEffect(() => {
    if (selected) {
      const activeElement = document.activeElement
      const currentItem = itemRefs.current.find(ref => {
        return ref === activeElement
      })
      if (currentItem) {
        currentItem.blur()
      }
      const eventListener = (event: KeyboardEvent): void => {
        switch (event.key) {
          case 'Tab':
          case 'Escape':
            const el = itemRefs.current?.find(ref => {
              return ref?.contains(document.activeElement)
            })

            if (el) {
              setShowFocus(true)
              el.focus()
            }

            setSelected(undefined)
            event.preventDefault()
            break
          default:
            break
        }
      }
      document.addEventListener('keydown', eventListener)
      return (): void => document.removeEventListener('keydown', eventListener)
    }
  }, [selected, showFocus, setShowFocus, setSelected])

  return { itemRefs, onMouseMove, onMouseLeave }
}
